import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import './ConnessionePersaBanner.css';

interface ConnessionePersaBannerProps {
  /** C'e' una partita in mano, ma lo stato non e' giocabile ne' concluso. */
  attiva: boolean;
  /** Richiede al server lo stato vero della partita. */
  onRiprova: () => void;
  /** Lascia la partita e torna alla home. */
  onEsci: () => void;
}

/**
 * Quanto si aspetta prima di mostrarsi.
 *
 * Un blip di rete dura meno di questo, e un avviso che lampeggia a ogni
 * singhiozzo e' rumore. Chi rientra dal secondo piano invece resta in questo
 * stato per secondi interi: la' l'avviso serve, ed e' l'unica cosa che spiega
 * perche' la damiera non risponde piu'.
 */
const ATTESA_PRIMA_DI_MOSTRARSI_MS = 2000;

/**
 * «Connessione persa» sopra la damiera.
 *
 * Nasce dal difetto opposto: prima, in questo stato, non compariva NIENTE. La
 * damiera restava afferrabile, le mosse non andavano da nessuna parte e il
 * bottone «Abbandona» non produceva effetti — uno schermo che prometteva azioni
 * che non faceva. Qui si dice come stanno le cose e si danno le due sole vie
 * d'uscita che hanno senso: riprovare, o andarsene.
 */
export default function ConnessionePersaBanner({ attiva, onRiprova, onEsci }: ConnessionePersaBannerProps) {
  const { t } = useTranslation();
  const [mostra, setMostra] = useState(false);

  useEffect(() => {
    if (!attiva) {
      setMostra(false);
      return;
    }
    const id = window.setTimeout(() => setMostra(true), ATTESA_PRIMA_DI_MOSTRARSI_MS);
    return () => window.clearTimeout(id);
  }, [attiva]);

  if (!mostra) return null;

  return (
    <div className="connessione-persa" role="alert" data-testid="connessione-persa">
      <span className="cp-icona" aria-hidden="true"><WifiOff size={18} /></span>
      <div className="cp-testo">
        <span className="cp-titolo">{t('online.connessionePersa.titolo', 'Connection lost')}</span>
        <span className="cp-corpo">{t('online.connessionePersa.corpo', 'Trying to get you back to the board.')}</span>
      </div>
      <div className="cp-azioni">
        <button type="button" className="cp-bottone cp-riprova" onClick={onRiprova}>
          {t('online.connessionePersa.riprova', 'Retry')}
        </button>
        <button type="button" className="cp-bottone cp-esci" onClick={onEsci}>
          {t('online.connessionePersa.esci', 'Leave')}
        </button>
      </div>
    </div>
  );
}
